'use client'

import React, { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Building2, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react'

export function SubscribeOrgForm({ clientIp }: { clientIp: string }) {
  const router = useRouter()
  const [name, setName] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [emailDomain, setEmailDomain] = useState('')
  const [initialCidr, setInitialCidr] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await fetch('/api/wlib-org', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'signup-organization',
          name,
          adminName,
          adminEmail,
          emailDomain,
          initialCidr,
        }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        setError(data.error || 'Could not start institutional trial.')
      } else {
        router.push(data.redirectTo || '/org')
        router.refresh()
      }
    } catch (err: any) {
      setError(err?.message || 'Network error.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="wl-card" style={{ border: '2px solid var(--gold-primary)' }}>
      <div className="wl-eyebrow">
        <Building2 size={15} />
        <span>Instant 30-Day Institutional &amp; Church Trial</span>
      </div>
      <h2 style={{ fontSize: '1.6rem', marginBottom: '0.5rem' }}>
        Provision Your Campus or Church Portal in 30 Seconds
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.25rem', fontSize: '0.95rem' }}>
        Create your organization profile now to unlock immediate IP range recognition, a shareable
        Campus Access Code, and <code>.edu</code> Magic Link authentication.
      </p>

      {error && (
        <div className="wl-alert wl-alert-error">
          <AlertCircle size={18} />
          <div>{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="wl-field">
          <label className="wl-label">Seminary, University, or Church Name *</label>
          <input
            type="text"
            required
            className="wl-input"
            placeholder="e.g. Duke Divinity School or Trinity Presbyterian Church"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="wl-grid-2">
          <div className="wl-field">
            <label className="wl-label">Administrator / Librarian Name *</label>
            <input
              type="text"
              required
              className="wl-input"
              placeholder="Dr. Thomas Cranmer"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
            />
          </div>
          <div className="wl-field">
            <label className="wl-label">Administrator Email *</label>
            <input
              type="email"
              required
              className="wl-input"
              placeholder="librarian@seminary.edu"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="wl-grid-2">
          <div className="wl-field">
            <label className="wl-label">Allowed Email Domain (Optional)</label>
            <input
              type="text"
              className="wl-input"
              placeholder="e.g. divinity.duke.edu"
              value={emailDomain}
              onChange={(e) => setEmailDomain(e.target.value)}
            />
          </div>
          <div className="wl-field">
            <label className="wl-label">Campus IP or CIDR (Optional)</label>
            <div style={{ display: 'flex', gap: '0.45rem' }}>
              <input
                type="text"
                className="wl-input"
                placeholder="e.g. 192.168.50.0/24"
                value={initialCidr}
                onChange={(e) => setInitialCidr(e.target.value)}
              />
              <button
                type="button"
                className="wl-btn wl-btn-outline wl-btn-sm"
                onClick={() => setInitialCidr(clientIp)}
              >
                Use {clientIp}
              </button>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="wl-btn wl-btn-primary wl-btn-lg"
          style={{ width: '100%' }}
        >
          <span>
            {loading
              ? 'Activating Institutional Portal...'
              : 'Launch Instant Institutional Trial & Open /org Dashboard'}
          </span>
          <ArrowRight size={17} />
        </button>

        <div
          style={{
            marginTop: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.82rem',
            color: 'var(--text-muted)',
            justifyContent: 'center',
          }}
        >
          <CheckCircle2 size={14} style={{ color: 'var(--green-text)' }} />
          <span>No credit card required for 30-day evaluation • Phase 2 Stripe invoicing ready</span>
        </div>
      </form>
    </div>
  )
}
